import {useQuery} from 'react-query'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {useRouter} from 'next/router'
import {useState, useEffect} from 'react'
import {isHttpError} from '@/services/http'
import {BusinessMapWithSearchFilterAndInfoWindow as GoogleBusinessMap} from '@/products/find-a-business/business-map-with-search-filter-and-info-window'
import {useFetchBusinessMapMarkers} from '@/services/find-a-business/client'
import type {Location, Marker} from '@/models/page-props'
import {env} from '@/services/env/client'
import type {IconContentItem} from '@/products/flexible-web-toolkit/blocks/icon'
import type {Block, Tersed} from '@/services/kontent-ai/types'
import {BUSINESS_TYPE_MAP} from '@/products/find-a-business/constants'
import {LoadingSpinner} from '@/products/flexible-web-toolkit/components/loading-spinner'
import {logEvent, logError} from '@/services/logger/client'
import type {FilterState, LocationState} from '@/utilities/types'
import {
	getHexCode,
	getSafeHexCode,
} from '@/products/flexible-web-toolkit/colors/registry'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {COUNTRIES_MAP} from './country-map'

const stringifyLocation = (location: Location): string => {
	let address = ''
	address =
		address +
		(location.address ? location.address : '') +
		(location.address ? '\n' : '') +
		(location.address2 ? location.address2 : '') +
		(location.address2 ? '\n' : '') +
		(location.city ? location.city : '') +
		(location.city ? '\n' : '') +
		(location.postalCode ? location.postalCode : '') +
		(location.postalCode ? '\n' : '') +
		(location.state ? location.state : '') +
		(location.state ? '\n' : '') +
		(location.countryCode ? location.countryCode : '') +
		(location.countryCode ? '\n' : '')
	return address
}

/**
 * @returns A mapping of typeId codename to the image URL for that marker
 */
const getImagesFromBusinessTypes = (
	types: Tersed<BusinessTypeItemContentItem>[]
): Record<string, string> => {
	const data: Record<string, string> = {}
	types.forEach((businessType) => {
		const typeId = businessType.elements.typeId[0]
		const icons = businessType.elements.icons
		if (typeId) {
			if (icons.length > 0) {
				const icon = icons[0]
				const image = icon?.elements.icon[0]

				if (image) data[typeId.name] = image.url
			} else {
				data[typeId.name] = ''
			}
		}
	})
	return data
}

type BusinessTypeItemContentItem = IContentItem<{
	displayName: Elements.TextElement
	typeId: Elements.MultipleChoiceElement
	preselectedFilter: Elements.MultipleChoiceElement
	icons: Elements.LinkedItemsElement<IconContentItem>
}>

type ProductTagContentItem = IContentItem<{
	displayName: Elements.TextElement
	palId: Elements.TextElement
	preselectedFilter: Elements.MultipleChoiceElement
}>

export type FindABusinessContentItem = IContentItem<{
	mapTitle: Elements.TextElement
	mapDescription: Elements.TextElement
	northBoundPoint: Elements.NumberElement
	eastBoundPoint: Elements.NumberElement
	southBoundPoint: Elements.NumberElement
	westBoundPoint: Elements.NumberElement
	radius: Elements.NumberElement
	label: Elements.TextElement
	placeholderText: Elements.TextElement
	locationText: Elements.TextElement
	allowedCountries: Elements.MultipleChoiceElement
	businessFilterTitle: Elements.TextElement
	businessType: Elements.LinkedItemsElement<BusinessTypeItemContentItem>
	tagsFilterTitle: Elements.TextElement
	tags: Elements.LinkedItemsElement<ProductTagContentItem>
	apiError: Elements.TextElement
	directionsText: Elements.TextElement
	filterButtonText: Elements.TextElement
	filterResetText: Elements.TextElement
	enterLocationPrompt: Elements.RichTextElement
	areaAccentColour: Elements.MultipleChoiceElement
	areaOpacity: Elements.NumberElement
	areaAccentColourCustom: Elements.CustomElement
}>

export const FindABusinessBlock: Block<FindABusinessContentItem> = ({
	block,
	page,
}) => {
	const {query} = useRouter()
	const {colorId: finalColorId} = colorWithFallback({
		blockColor: block.elements.areaAccentColourCustom,
		blockColorLegacy: block.elements.areaAccentColour,
		pageConfig: page.elements.config[0],
		fallbackHexColor: '#ffffff',
	})

	const radius = block.elements.radius ?? 50
	const businessTypeIds: string[] = []
	const productFilter = query.filter?.toString()
	const filteredTags = productFilter
		? block.elements.tags.map((tag) => ({
				...tag,
				preselected_filter:
					tag.elements.displayName === productFilter
						? {boolean: true, choice: 'yes'}
						: {boolean: false, choice: 'no'},
			}))
		: block.elements.tags

	const businessTypeFilter = query.businessType?.toString()
	const filteredBusinessTypes = businessTypeFilter
		? block.elements.businessType.map((bt) => ({
				...bt,
				preselected_filter:
					bt.elements.displayName === businessTypeFilter
						? {boolean: true, choice: 'yes'}
						: {boolean: false, choice: 'no'},
			}))
		: block.elements.businessType

	const businessesType = filteredBusinessTypes.map((bT) => ({
		display_name: bT.elements.displayName,
		preselected_filter:
			bT.elements.preselectedFilter[0]?.codename === 'yes',
		type_id: {
			choice: bT.elements.typeId[0]?.name || '',
		},
		icons: bT.elements.icons,
	}))

	const tagsId = filteredTags.map((tag) => ({
		preselected_filter: {
			boolean: tag.elements.preselectedFilter[0]?.codename === 'yes',
		},
		pal_id: tag.elements.palId,
		display_name: tag.elements.displayName,
	}))

	const businessType = block.elements.businessType
		.map<Tersed<BusinessTypeItemContentItem> | null>((type) => {
			const typeId = type.elements.typeId[0]?.name
			if (!typeId) return null

			businessTypeIds.push(typeId)

			const mapped = BUSINESS_TYPE_MAP.get(typeId)
			if (mapped)
				return {
					...type,
					elements: {
						...type.elements,
						typeId: [
							{
								codename: mapped,
								name: type.elements.typeId[0]?.name ?? '',
							},
						],
					},
				}

			return type
		})
		.filter(Boolean)

	const [locationState, setLocationState] = useState<LocationState>({
		permission: 'prompt',
		enableSearch: false,
	})
	const enterLocationPromptValue = block.elements.enterLocationPrompt.value

	const defaultPromptMessage =
		'<p>Please enter your location in the search box to see clinics near you</p>'

	const promptContent =
		enterLocationPromptValue && enterLocationPromptValue !== '<p><br></p>'
			? enterLocationPromptValue
			: defaultPromptMessage

	const enterLocationPromptContent = enterLocationPromptValue
		? promptContent
		: ''

	const finalLocationPromptValue =
		locationState.permission !== 'granted' ? enterLocationPromptContent : ''
	const [searchLocationLatLong, setSearchLocationLatLong] = useState<{
		searchName?: string
		lat: number
		lng: number
	}>({
		searchName: '',
		lat: 0,
		lng: 0,
	})

	const [filterState, setFilterState] = useState<FilterState>({
		businessTypes: businessesType,
		tags: tagsId,
	})
	const handleFilterUpdate = (newState: FilterState): void => {
		setFilterState(newState)
	}

	const typeIdsString = businessTypeIds.join()
	const tagIdsString = block.elements.tags
		.filter((tag) => tag.elements.palId && tag.elements.palId.length > 0)
		.map((tag) => tag.elements.palId)
		.join()

	const getCountryCodes = (): string[] => {
		return block.elements.allowedCountries.map(
			(country) =>
				COUNTRIES_MAP[country.codename as keyof typeof COUNTRIES_MAP]
		)
	}

	const lat = searchLocationLatLong.lat
	const lon = searchLocationLatLong.lng

	useEffect(() => {
		const checkGeolocationAndSetSearch = async (): Promise<void> => {
			try {
				const permission = await navigator.permissions.query({
					name: 'geolocation',
				})

				if (permission.state === 'granted') {
					navigator.geolocation.getCurrentPosition(
						(position) => {
							const {latitude, longitude} = position.coords
							setSearchLocationLatLong({
								searchName: 'Current Location',
								lat: latitude,
								lng: longitude,
							})
							setLocationState({
								permission: 'granted',
								enableSearch: true,
							})
						},
						(error) => {
							logError(
								new Error('Error getting current position:', {
									cause: error,
								})
							)

							setLocationState({
								permission: 'denied',
								enableSearch: false,
							})
						},
						{timeout: 10000, enableHighAccuracy: true}
					)
				} else if (permission.state === 'denied') {
					setLocationState({
						permission: 'denied',
						enableSearch: false,
					})
				}
			} catch (error) {
				logError(
					new Error('Error checking geolocation permission:', {
						cause: error,
					})
				)
			}
		}

		void checkGeolocationAndSetSearch()
	}, [])

	useEffect(() => {
		logEvent({
			name: 'FIND_A_BUSINESS_CONFIGURED',
			properties: {
				title: block.elements.mapTitle,
				businessType,
				kontentCodename: block.system.codename,
				allowedCountries: block.elements.allowedCountries.map(
					(country) =>
						COUNTRIES_MAP[
							country.codename as keyof typeof COUNTRIES_MAP
						]
				),
			},
		})
	}, [
		block.elements.allowedCountries,
		block.elements.mapTitle,
		block.system.codename,
		businessType,
	])

	const {
		data: mapMarkers,
		error: mapError,
		isFetching,
	} = useQuery(
		[
			'businessMap',
			{
				tagIdsString,
				typeIdsString,
				lat,
				lon,
				radius,
			},
		],
		useFetchBusinessMapMarkers,
		{
			refetchOnWindowFocus: false,
			cacheTime: 15 * (60 * 1000),
			enabled: locationState.enableSearch,
		}
	)

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- implicit is fine here
	const convertBasicMarkersIntoPlainMarkers = (basicMarkers: Marker[]) => {
		const codenamesToMarkerImages = getImagesFromBusinessTypes(businessType)
		const filteredItems = basicMarkers.filter(
			(item) =>
				item.location?.latitude &&
				item.location.longitude &&
				item.location.latitude > -90.0 &&
				item.location.latitude < 90.0 &&
				item.location.longitude > -180.0 &&
				item.location.longitude < 180.0
		)
		const allMarkers = filteredItems.map((basicMarker) => {
			const {
				location,
				accountClassification,
				accountId,
				accountPhone,
				tags: localTags,
				websiteUrl,
				name,
			} = basicMarker
			const address = location ? stringifyLocation(location) : ''
			let imageURL = ''

			if (accountClassification) {
				const markerImage =
					codenamesToMarkerImages[accountClassification]
				if (markerImage) {
					imageURL = markerImage
				}
			}

			return {
				accountClassification,
				accountId,
				accountPhone,
				tags: localTags,
				lat: location?.latitude,
				lng: location?.longitude,
				address,
				websiteUrl,
				name,
				image: imageURL,
			}
		})
		return allMarkers
	}

	let mappedMarkers: Marker[] = []
	if (mapMarkers && mapMarkers.length > 0) {
		mappedMarkers = convertBasicMarkersIntoPlainMarkers(mapMarkers)
	}

	if (mapError) {
		logError(
			new Error(
				`Error occurred in Find-a-business api, ${
					isHttpError(mapError) ? mapError.message : ''
				}`
			)
		)
		return (
			<div data-kontent-element-codename="api_error">
				<h1>{block.elements.apiError}</h1>;
			</div>
		)
	}

	return (
		<div data-kontent-element-codename="find_a_business">
			{isFetching ? (
				<div className="flex h-[800px] w-full animate-pulse items-center justify-center rounded bg-elanco-blue bg-opacity-20 p-5">
					<LoadingSpinner theme="blue" />
				</div>
			) : null}
			<div style={{display: isFetching ? 'none' : 'block'}}>
				<GoogleBusinessMap
					apiKey={env.NEXT_PUBLIC_MAP_API_KEY}
					businessFilterTitle={block.elements.businessFilterTitle}
					businessTypes={businessesType}
					countries={getCountryCodes()}
					currentLocationText={block.elements.locationText}
					defaultEastBound={block.elements.eastBoundPoint || 0}
					defaultNorthBound={block.elements.northBoundPoint || 0}
					defaultSouthBound={block.elements.southBoundPoint || 0}
					defaultWestBound={block.elements.westBoundPoint || 0}
					description={block.elements.mapDescription}
					directionsText={block.elements.directionsText}
					enableSearch={locationState.enableSearch}
					enterLocationPrompt={finalLocationPromptValue}
					filterButtonText={block.elements.filterButtonText}
					filterResetText={block.elements.filterResetText}
					filterState={filterState}
					handleFilterUpdate={handleFilterUpdate}
					label={block.elements.label}
					locationState={locationState}
					markers={mappedMarkers}
					permission={locationState.permission}
					placeholderText={block.elements.placeholderText}
					radius={radius}
					searchAreaAccentColour={getSafeHexCode(
						getHexCode(finalColorId ?? '') ?? '',
						'#ffffff'
					)}
					searchAreaOpacity={block.elements.areaOpacity}
					searchLocationLatLong={searchLocationLatLong}
					setLocationState={setLocationState}
					setSearchLocationLatLong={setSearchLocationLatLong}
					tags={tagsId}
					tagsFilterTitle={block.elements.tagsFilterTitle}
					title={block.elements.mapTitle}
				/>
			</div>
		</div>
	)
}
